import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Dropdown from "@shared/v2/Dropdown";
import { useDebounce } from "@shared/hookHelpers";
import { MagnifyingGlassSolidV6 } from "../../shared/v2/Icomoon";

const DEFAULT_SCOPE = "account";

const getQueryKey = (scope) => {
  const scopes = { account: "search", teamMembers: "q" };
  return scopes[scope] || scopes[DEFAULT_SCOPE];
};

const getScopeKey = (scope) => {
  const scopes = {
    account: "account_people",
    teamMembers: "team_members",
  };
  return scopes[scope] || scopes[DEFAULT_SCOPE];
};

const searchPeople = ({ scope, search, signal }) =>
  axios
    .get(`/search/${getScopeKey(scope)}`, { signal, params: { [getQueryKey(scope)]: search } })
    .then((res) => (res.data.people || res.data).map((p) => ({ ...p, value: p.uuid })));

const useSearchPeople = ({ scope, search, excludeTrashAndArchived }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    if (!search) return;
    const abortController = new AbortController();
    searchPeople({ scope, search: debouncedSearch, signal: abortController.signal })
      .then((d) => {
        setLoading(false);
        setData(excludeTrashAndArchived ? d.filter((p) => !["trash", "archived"].includes(p.status)) : d);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setLoading(false);
      });
    setLoading(true);
  }, [debouncedSearch, excludeTrashAndArchived, scope]);

  return { data, loading };
};

const SearchPeople = ({
  labelClassName,
  searchBoxValue: propSearchBoxValue,
  onSelectFunction,
  scope,
  excludeTrashAndArchived,
  ...props
}) => {
  const [searchBoxValue, setSearchBoxValue] = useState(propSearchBoxValue || "");
  const [searchTerm, setSearchTerm] = useState("");
  const { data, loading } = useSearchPeople({ scope, search: searchTerm, excludeTrashAndArchived });

  useEffect(() => {
    setSearchBoxValue(propSearchBoxValue);
  }, [propSearchBoxValue]);

  const handleSearchChange = (value) => {
    setSearchBoxValue(value);
    setSearchTerm(value);
  };

  const handleClick = (option) => {
    setSearchBoxValue(option?.label || "");
    onSelectFunction(option);
  };

  return (
    <div className={`${labelClassName} tw-pb-[4px] tw-pt-[8px]`}>
      <Dropdown
        isLoading={loading}
        isSearchable
        placeholder="Begin typing a person's name"
        inputValue={searchBoxValue}
        onInputChange={handleSearchChange}
        shouldFilterOptions={false}
        options={data}
        onChange={handleClick}
        menuShouldComeToFront
        inputTrailing={<MagnifyingGlassSolidV6 />}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    </div>
  );
};

SearchPeople.propTypes = {
  labelClassName: PropTypes.string,
  searchBoxValue: PropTypes.string,
  onSelectFunction: PropTypes.func.isRequired,
  scope: PropTypes.oneOf(["account", "teamMembers"]),
  excludeTrashAndArchived: PropTypes.bool,
};

SearchPeople.defaultProps = {
  scope: "account",
  labelClassName: "",
  searchBoxValue: "",
  excludeTrashAndArchived: false,
};

export default SearchPeople;
